import { Controller } from '@hotwired/stimulus'

// Chrome del modo Revisar: las vistas «Original · Cambios · Resultado» (la
// clase is-resultado oculta lo eliminado y neutraliza lo añadido; el original
// es el publicado renderizado aparte), el preview de solo lectura pero
// seleccionable, la copia limpia y la recuperación de la copia local del
// editor tras un fallo de guardado.
//
// Connects to data-controller="revision-borrador"

// Lo que el tabulador no debe recorrer dentro de un preview.
const ENFOCABLES = 'a[href], area[href], button, input, select, textarea, iframe, summary, ' +
                   '[tabindex], [contenteditable]'
// Lo que oculta la vista «Resultado»: la misma lista que `.is-resultado` en edit_borrador.scss.
const OCULTO_EN_RESULTADO = '.rnk-Diff-removed, .rnk-DiffCard--eliminado, .rnk-DiffCard_Version--anterior'
const CHROME_DE_TARJETA = '.rnk-DiffCard_Head, .rnk-DiffCard_VersionLabel, .rnk-DiffCard_SinVista'
// Solo las marcas del diff: un <del> del propio artículo es formato y se conserva.
const MARCAS = 'del.rnk-Diff-removed, ins.rnk-Diff-added'
const BLOQUES = 'p, div, section, h1, h2, h3, h4, h5, h6, li, tr, blockquote, pre, figure'

export default class extends Controller {
  static targets = ['recuperar', 'diff', 'original', 'preview']
  static values = { clave: String }

  initialize () {
    this.observadores = new Map()
  }

  connect () {
    if (this.hasRecuperarTarget) {
      this.muestraRecuperarSiHayCopia()
    } else if (this.claveValue) {
      // Carga sin fallo previo: purgar la copia local evita ofrecer luego la
      // recuperación de una versión obsoleta.
      localStorage.removeItem(this.claveValue)
    }
  }

  cambiaVista ({ target: { value: vista } }) {
    this.element.classList.toggle('is-resultado', vista === 'resultado')
    this.diffTarget.hidden = vista === 'original'
    this.originalTarget.hidden = vista !== 'original'
  }

  // Los controllers de los widgets (table-slider, simulador-pension) añaden
  // botones y enlaces al conectar, después que este.
  previewTargetConnected (preview) {
    sacaDelTabulador(preview)
    const observador = new MutationObserver(() => sacaDelTabulador(preview))
    observador.observe(preview, { childList: true, subtree: true })
    this.observadores.set(preview, observador)
  }

  previewTargetDisconnected (preview) {
    this.observadores.get(preview)?.disconnect()
    this.observadores.delete(preview)
  }

  // Ningún clic llega a lo que pinta el preview: ni un enlace saca de la página
  // con lo no guardado en Editar, ni un widget reacciona. Seleccionar no
  // depende del clic, sino de pulsar y arrastrar.
  bloquea (evento) {
    evento.preventDefault()
    evento.stopPropagation()
  }

  copia (evento) {
    const seleccion = window.getSelection()
    if (!seleccion || seleccion.isCollapsed || !evento.clipboardData) return

    // En una <template> lo copiado es inerte: ni carga imágenes ni conecta controllers.
    const copiado = document.createElement('template')
    copiado.content.append(rangoConAdjuntosEnteros(seleccion.getRangeAt(0)).cloneContents())
    this.limpia(copiado.content)

    evento.clipboardData.setData('text/html', copiado.innerHTML)
    evento.clipboardData.setData('text/plain', textoPlano(copiado.content))
    evento.preventDefault()
  }

  // Un rango puede cruzar lo que no se ve (la otra vista, el panel Editar): la
  // copia nativa lo deja fuera y esta también.
  limpia (fragmento) {
    quita(fragmento, '[hidden]')
    if (this.element.classList.contains('is-resultado')) quita(fragmento, OCULTO_EN_RESULTADO)
    quita(fragmento, CHROME_DE_TARJETA)
    fragmento.querySelectorAll('[data-figura-trix]').forEach(adjunto => adjunto.replaceWith(figuraDe(adjunto)))
    fragmento.querySelectorAll('.rnk-Referencia').forEach(chip => chip.replaceWith(chip.dataset.referencia))
    fragmento.querySelectorAll(MARCAS).forEach(marca => marca.replaceWith(...marca.childNodes))
  }

  muestraRecuperarSiHayCopia () {
    if (this.claveValue && localStorage.getItem(this.claveValue)) {
      this.recuperarTarget.hidden = false
    }
  }

  recuperar () {
    const guardado = localStorage.getItem(this.claveValue)
    const editor = this.element.querySelector('trix-editor')
    if (!guardado || !editor || !editor.editor) return

    try {
      editor.editor.loadJSON(JSON.parse(guardado))
      this.recuperarTarget.hidden = true
    } catch {
      // Copia corrupta: mejor dejar el aviso visible que romper el editor.
    }
  }
}

function sacaDelTabulador (raiz) {
  raiz.querySelectorAll(ENFOCABLES).forEach(elemento => elemento.setAttribute('tabindex', '-1'))
}

function quita (fragmento, selector) {
  fragmento.querySelectorAll(selector).forEach(elemento => elemento.remove())
}

function adjuntoDe (nodo) {
  const elemento = nodo.nodeType === Node.ELEMENT_NODE ? nodo : nodo.parentElement
  return elemento?.closest('[data-figura-trix]')
}

// Medio widget no se puede pegar: si la selección empieza o acaba dentro de
// uno, se amplía a él entero. La que queda dentro de un único widget es texto.
function rangoConAdjuntosEnteros (rango) {
  const ampliado = rango.cloneRange()
  const inicio = adjuntoDe(rango.startContainer)
  const fin = adjuntoDe(rango.endContainer)
  if (inicio === fin) return ampliado

  if (inicio) ampliado.setStartBefore(inicio)
  if (fin) ampliado.setEndAfter(fin)
  return ampliado
}

// El <figure> de Trix del adjunto, el que carga Editar: Trix lo pega como el
// mismo widget o la misma imagen. Lo emite el servidor; se parsea en una
// <template>, sin pasar por la página.
function figuraDe (adjunto) {
  const plantilla = document.createElement('template')
  plantilla.innerHTML = adjunto.dataset.figuraTrix
  return plantilla.content
}

// Un salto por bloque, como el texto plano del portapapeles nativo.
function textoPlano (fragmento) {
  const copia = fragmento.cloneNode(true)
  copia.normalize()
  const textos = copia.ownerDocument.createTreeWalker(copia, NodeFilter.SHOW_TEXT)
  while (textos.nextNode()) textos.currentNode.data = textos.currentNode.data.replace(/[ \t\r\n]+/g, ' ')
  copia.querySelectorAll('br').forEach(salto => salto.replaceWith('\n'))
  copia.querySelectorAll(BLOQUES).forEach(bloque => bloque.append('\n'))
  return copia.textContent.replace(/ *\n[ \n]*/g, '\n').trim()
}
